import * as React from 'react';
import { Theme, alpha } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import * as yup from 'yup';
import { useFormik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useUpdateSpotTypeMutation, Spot, UpdateSpotTypeInput } from '@graphql';
import { useToastError } from '@hooks';

interface RenderSwitchProps {
  type: keyof UpdateSpotTypeInput;
}
interface SpotTypeProps {
  isActive: boolean;
  account: Spot;
}

export function SpotType(props: Readonly<SpotTypeProps>) {
  const { account, isActive } = props;
  const toastError = useToastError();
  const { t } = useTranslation('cta');

  const [updateSpotTypeMutation] = useUpdateSpotTypeMutation();

  const initialValues = React.useMemo(() => {
    return Object.keys(account.type)
      .filter((key) => !['__typename', 'id', 'updatedAt'].includes(key))
      .reduce(
        (acc, key) => {
          acc[key as keyof UpdateSpotTypeInput] = account.type[key as RenderSwitchProps['type']] ?? null;
          return acc;
        },
        {} as Record<keyof UpdateSpotTypeInput, boolean | null>
      );
  }, [account.type]);

  const validationSchema = React.useMemo(() => {
    return yup.object(
      Object.keys(account.type)
        .filter((key) => !['__typename', 'id', 'updatedAt'].includes(key))
        .reduce(
          (acc, key) => {
            acc[key as keyof UpdateSpotTypeInput] = yup.boolean().nullable().defined();
            return acc;
          },
          {} as Record<keyof UpdateSpotTypeInput, yup.BooleanSchema<boolean | null>>
        )
    );
  }, [account.type]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      if (formik.dirty) {
        updateSpotTypeMutation({
          variables: { input: values },
          onCompleted: ({ updateSpotType }) => {
            formik.resetForm({ values });
            toast(t('spotType.toast.success', { ns: 'field' }), { type: 'success' });
          },
          onError: toastError,
        });
      }
    },
  });

  React.useEffect(() => {
    return () => {
      // trigger the mutation when the component is not anymore in view and the form is dirty
      if (formik.dirty) {
        formik.submitForm();
      }
    };
  }, [isActive, formik.dirty]);

  if (account?.__typename !== 'Spot') {
    return null;
  }

  return (
    <Stack width="100%" spacing={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          p: 3,
          borderRadius: (theme: Theme) => theme.borderRadius,
          backgroundColor: alpha('#fff', 0.1),
        }}
      >
        <Stack>
          <Typography fontWeight="bold">{t('spotType.label', { ns: 'field' })}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('spotType.caption', { ns: 'field' })}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        spacing={2}
        divider={<Divider sx={{ backgroundColor: (theme: Theme) => theme.palette.grey[800] }} />}
      >
        {Object.keys(account.type)
          .filter((key) => !['__typename', 'id', 'updatedAt'].includes(key))
          .map((type) => (
            <Stack
              key={type}
              spacing={2}
              direction="row"
              width="100%"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="caption" color="text.primary">
                {t(`type.${type}`, { ns: 'spot' })}
              </Typography>
              <RenderSwitch type={type as RenderSwitchProps['type']} formik={formik} />
            </Stack>
          ))}
      </Stack>
    </Stack>
  );
}

function RenderSwitch(props: RenderSwitchProps & { formik: FormikProps<any> }) {
  const { type, formik } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    formik.setFieldValue(name, checked);
  };

  return <Switch name={type} checked={!!formik.values[type]} onChange={handleChange} color={'success'} />;
}
